/**
 * =============================================================================
 * FALCON - Module Deneigement des Parkings Avion
 * =============================================================================
 *
 * @file        MobileMeteoWidget.tsx
 * @description Widget meteo compact pour l'interface mobile agent
 *              Affiche conditions meteo et risques hiver en un coup d'oeil
 *
 * @version     2.4
 * @author      Equipe FALCON
 * =============================================================================
 */

import { useState } from 'react';
import { useMetar, getWeatherIcon, formatVisibility } from '@/hooks/useMetar';
import { ArrowPathIcon, ExclamationTriangleIcon, ChevronDownIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { clsx } from 'clsx';

// Aeroports disponibles
const AIRPORTS = [
  { icao: 'LFPO', name: 'Paris-Orly', iata: 'ORY' },
  { icao: 'LFPG', name: 'Paris-CDG', iata: 'CDG' },
];

interface MobileMeteoWidgetProps {
  icao?: string;
}

/**
 * Widget Meteo compact pour mobile
 */
export function MobileMeteoWidget({ icao = 'LFPO' }: MobileMeteoWidgetProps) {
  const [selectedIcao, setSelectedIcao] = useState(icao);
  const [showDetail, setShowDetail] = useState(false);
  const { data: metar, isLoading, isError, refetch } = useMetar(selectedIcao);

  const currentAirport = AIRPORTS.find(a => a.icao === selectedIcao) || AIRPORTS[0];
  const alertLevel = metar?.winter_indicators?.alert_level ?? 'normal';

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 px-2 py-1 bg-white/20 rounded-lg animate-pulse">
        <div className="w-5 h-5 bg-white/30 rounded" />
        <div className="w-12 h-3 bg-white/30 rounded" />
      </div>
    );
  }

  if (isError || !metar) {
    return (
      <button
        onClick={() => refetch()}
        className="flex items-center gap-1 px-2 py-1 bg-white/20 rounded-lg text-white/80 text-xs"
      >
        <span>Meteo N/A</span>
        <ArrowPathIcon className="w-3 h-3" />
      </button>
    );
  }

  const weatherIcon = getWeatherIcon(metar);
  const hasRisks = metar.winter_indicators?.risque_givrage ||
                   metar.winter_indicators?.risque_verglas ||
                   metar.winter_indicators?.neige_active ||
                   metar.snoclo;

  return (
    <>
      {/* Widget compact - cliquable */}
      <button
        onClick={() => setShowDetail(true)}
        className={clsx(
          'flex items-center gap-1.5 px-2 py-1 rounded-lg text-xs transition-all',
          alertLevel === 'normal' && 'bg-green-500/20 text-white',
          alertLevel === 'vigilance' && 'bg-yellow-500/30 text-white',
          alertLevel === 'warning' && 'bg-amber-500/40 text-white',
          alertLevel === 'critique' && 'bg-red-500/50 text-white animate-pulse'
        )}
      >
        {/* Icone meteo + temp */}
        <span className="text-base">{weatherIcon}</span>
        <span className="font-bold">{metar.temperature ?? '--'}°</span>

        {/* Indicateur risque */}
        {hasRisks && (
          <ExclamationTriangleIcon className="w-4 h-4 text-yellow-300" />
        )}

        {/* Vent si fort */}
        {(metar.wind_speed ?? 0) >= 15 && (
          <span className="text-white/80">💨{metar.wind_speed}</span>
        )}
      </button>

      {/* Modal detail meteo */}
      {showDetail && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/50">
          <div
            className="w-full max-w-lg bg-white rounded-t-2xl shadow-xl animate-slide-up"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header modal */}
            <div className="flex items-center justify-between p-4 border-b">
              <div className="flex items-center gap-2">
                <span className="text-2xl">{weatherIcon}</span>
                <div>
                  <h3 className="font-bold text-gray-900">{currentAirport.name}</h3>
                  <span className="text-xs text-gray-700 font-medium">{metar.metar_tranche || metar.observed_time_utc || 'N/A'}</span>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {/* Selecteur aeroport */}
                <select
                  value={selectedIcao}
                  onChange={(e) => setSelectedIcao(e.target.value)}
                  className="px-2 py-1 text-sm font-semibold border border-gray-300 rounded-lg bg-white text-gray-900"
                >
                  {AIRPORTS.map((airport) => (
                    <option key={airport.icao} value={airport.icao}>
                      {airport.iata}
                    </option>
                  ))}
                </select>
                <button
                  onClick={() => refetch()}
                  className="p-2 hover:bg-gray-100 rounded-lg"
                >
                  <ArrowPathIcon className="w-5 h-5 text-gray-800" />
                </button>
                <button
                  onClick={() => setShowDetail(false)}
                  className="p-2 hover:bg-gray-100 rounded-lg"
                >
                  <XMarkIcon className="w-5 h-5 text-gray-800" />
                </button>
              </div>
            </div>

            {/* Contenu */}
            <div className="p-4 space-y-4">
              {/* Alerte si necessaire */}
              {alertLevel !== 'normal' && metar.winter_indicators?.alert_message && (
                <div className={clsx(
                  'p-3 rounded-lg text-sm font-semibold flex items-start gap-2',
                  alertLevel === 'vigilance' && 'bg-yellow-100 text-yellow-900 border border-yellow-300',
                  alertLevel === 'warning' && 'bg-amber-100 text-amber-900 border border-amber-300',
                  alertLevel === 'critique' && 'bg-red-100 text-red-900 border border-red-300'
                )}>
                  <ExclamationTriangleIcon className="w-5 h-5 flex-shrink-0 mt-0.5" />
                  <span>{metar.winter_indicators.alert_message}</span>
                </div>
              )}

              {/* Grille donnees principales */}
              <div className="grid grid-cols-4 gap-2">
                <div className="bg-gray-100 p-3 rounded-xl text-center">
                  <div className="text-xs text-gray-700 font-medium mb-1">Temp</div>
                  <div className="text-xl font-bold text-gray-900">{metar.temperature ?? '--'}°</div>
                </div>
                <div className="bg-gray-100 p-3 rounded-xl text-center">
                  <div className="text-xs text-gray-700 font-medium mb-1">Rosee</div>
                  <div className="text-xl font-bold text-gray-900">{metar.dewpoint ?? '--'}°</div>
                </div>
                <div className="bg-gray-100 p-3 rounded-xl text-center">
                  <div className="text-xs text-gray-700 font-medium mb-1">Humid</div>
                  <div className="text-xl font-bold text-gray-900">{metar.humidity ?? '--'}%</div>
                </div>
                <div className="bg-gray-100 p-3 rounded-xl text-center">
                  <div className="text-xs text-gray-700 font-medium mb-1">QNH</div>
                  <div className="text-lg font-bold text-gray-900">{metar.qnh ?? '--'}</div>
                </div>
              </div>

              {/* Vent */}
              <div className="flex items-center justify-between p-3 bg-gray-100 rounded-xl">
                <span className="text-gray-800 font-medium">💨 Vent</span>
                <span className="font-semibold text-gray-900">
                  {metar.wind_speed != null ? (
                    <>
                      {metar.wind_dir_text || 'Variable'} {metar.wind_speed}kt
                      {metar.wind_gust && (
                        <span className="text-amber-700 ml-1">(raf. {metar.wind_gust}kt)</span>
                      )}
                    </>
                  ) : (
                    'Calme'
                  )}
                </span>
              </div>

              {/* Visibilite */}
              <div className="flex items-center justify-between p-3 bg-gray-100 rounded-xl">
                <span className="text-gray-800 font-medium">👁 Visibilite</span>
                <span className="font-semibold text-gray-900">{formatVisibility(metar.visibility)}</span>
              </div>

              {/* Conditions meteo */}
              {metar.weather_report && (
                <div className="flex items-center justify-between p-3 bg-gray-100 rounded-xl">
                  <span className="text-gray-800 font-medium">☁️ Conditions</span>
                  <span className="font-semibold text-gray-900 text-right max-w-[60%]">{metar.weather_report}</span>
                </div>
              )}

              {/* Risques hiver */}
              <div className="p-3 bg-gray-100 rounded-xl">
                <div className="text-xs text-gray-700 font-medium mb-2">Risques Hiver</div>
                <div className="flex flex-wrap gap-2">
                  {metar.winter_indicators?.risque_givrage && (
                    <span className="px-2 py-1 rounded-lg bg-blue-200 text-blue-900 text-sm font-semibold">
                      ❄️ Givrage
                    </span>
                  )}
                  {metar.winter_indicators?.risque_verglas && (
                    <span className="px-2 py-1 rounded-lg bg-cyan-200 text-cyan-900 text-sm font-semibold">
                      🧊 Verglas
                    </span>
                  )}
                  {metar.winter_indicators?.neige_active && (
                    <span className="px-2 py-1 rounded-lg bg-indigo-200 text-indigo-900 text-sm font-semibold">
                      🌨️ Neige active
                    </span>
                  )}
                  {metar.snoclo && (
                    <span className="px-2 py-1 rounded-lg bg-red-200 text-red-900 text-sm font-bold animate-pulse">
                      ⛔ SNOCLO
                    </span>
                  )}
                  {!hasRisks && (
                    <span className="px-2 py-1 rounded-lg bg-green-200 text-green-900 text-sm font-semibold">
                      ✅ Aucun risque
                    </span>
                  )}
                </div>
              </div>

              {/* Statut operations */}
              <div className={clsx(
                'p-3 rounded-xl text-center font-semibold',
                metar.winter_indicators?.conditions_operables
                  ? 'bg-green-200 text-green-900'
                  : 'bg-red-200 text-red-900'
              )}>
                {metar.winter_indicators?.conditions_operables
                  ? '✓ Conditions operables'
                  : '✗ Conditions non operables'}
              </div>

              {/* Footer */}
              {(metar.sunrise || metar.sunset) && (
                <div className="flex justify-between text-xs text-gray-600 pt-2">
                  <span>
                    {metar.sunrise && `🌅 Lever ${metar.sunrise}`}
                    {metar.sunrise && metar.sunset && ' - '}
                    {metar.sunset && `🌇 Coucher ${metar.sunset}`}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default MobileMeteoWidget;
